import { useQueryClient, useQuery, useMutation, useQueries } from 'react-query';
import {
  addRole,
  getLinkedOrgs,
  getOrganizationMemberRoles,
  getOrgProfile,
  removeRole,
  updateOrganizationInfo,
  updateCustomerPhone,
  updateCustomerEmail,
  createCustomerPhone,
  createCustomerEmail,
  getPCOGroups,
  getPCOGroupDetails,
  importPCOGroup,
  updateTaxLegalName,
  addOrgTaxExemption,
  getOrganizationAssociatedMembers,
  requestAccess,
  getOrganizationAccessRequests,
} from '../api/organizationApi';
import { formatMemberRoles, formatRoleName } from '../../utils/orgHelpers';
import {
  getOrganizationMemberRolesPending,
  getUserRolesPending,
  onError,
  setBannerState,
  updateLinkedOrganizationsPending,
  updateLinkedOrganizationsSuccess,
} from '../../actions/AppActions';
import intl from '../../actions/lang/en.json';
import { ROLES, ENABLE_PCO_INTEGRATION, ENABLE_ACCESS_REQUESTS } from '../../constants';
import { buildAddRoleCommand } from '../../logic/linkedOrganizationsLogic';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentUserId } from '../../selectors/PersonProfileSelectors';

export function useGetOrganizationById(organizationId) {
  const {
    data: linkedOrgResponse,
    isLoading: isLinkedOrgLoading,
    isError: isLinkedOrgError,
  } = useQuery({
    queryKey: ['currentUser', 'linkedOrg', organizationId],
    queryFn: () => getLinkedOrgs(),
    staleTime: Infinity,
    retry: false,
  });
  const linkedOrg = linkedOrgResponse?.data?.find(
    (org) => org.organizationId === organizationId
  );

  return {
    data: linkedOrg || {},
    isLoading: isLinkedOrgLoading,
    isError: isLinkedOrgError,
    hasLinkedOrg: !!linkedOrg,
  };
}

export function useGetLinkedOrgs() {
  const {
    data: allLinkedOrgsResponse,
    isLoading: isLinkedOrgsLoading,
    isError: isLinkedOrgsError,
  } = useQuery({
    queryKey: ['currentUser', 'linkedOrgs'],
    queryFn: () => getLinkedOrgs(),
    staleTime: Infinity,
    retry: false,
  });
  const allLinkedOrgs = allLinkedOrgsResponse?.data;

  return {
    data: allLinkedOrgs,
    hasLinkedOrgs: allLinkedOrgs?.length > 0,
    isLoading: isLinkedOrgsLoading,
    isError: isLinkedOrgsError,
  };
}

export function useGetOrgLegacyFlag(organizationId, enablePolling) {
  const { data: linkedOrgs, isLoading, isError } = useQuery({
    queryKey: ['currentUser', 'linkedOrgs'],
    queryFn: () => getLinkedOrgs(),
    staleTime: Infinity,
    enabled: enablePolling === true,
    refetchInterval: (query) => {
      if (query?.data) {
        const org = query?.data?.find(
          (org) => org.organizationId === organizationId
        );
        const isInLegacySystem = org?.isInLegacySystem;
        if (isInLegacySystem) {
          return false;
        } else {
          return 5000;
        }
      }
      return 5000;
    },
    refetchIntervalInBackground: true,
    retry: false,
  });
  const org = linkedOrgs?.data?.find(
    (org) => org.organizationId === organizationId
  );
  const isInLegacySystem = org?.isInLegacySystem;

  return {
    data: isInLegacySystem,
    isLoading,
    isError,
  };
}

export function useGetOrganizationMembers(organizationId) {
  const { data: linkedOrgs, isLoading, isError } = useGetLinkedOrgs();
  const org = linkedOrgs?.find((org) => org.organizationId === organizationId);
  const orgMembers = org?.groups?.find(
    (group) => group.name === 'Organization Members'
  );

  return {
    data: orgMembers?.members,
    isLoading,
    isError,
  };
}

export function useOrganizationProfile(organizationId, select, options = {}) {
  return useQuery({
    queryKey: ['organizationProfile', organizationId],
    queryFn: () => getOrgProfile(organizationId),
    enabled: !!organizationId,
    select,
    ...options,
  });
}

export function useOrganizationProfileData(organizationId) {
  const {
    data: orgProfile,
    isLoading,
    isError,
    refetch,
  } = useOrganizationProfile(organizationId);

  return {
    isLoading,
    isError,
    data: orgProfile?.profile?.OrganizationProfile || {},
    refetch,
  };
}

export function useGetOrganizationAssociatedMembers(
  organizationId,
  orgRouteToken
) {
  const {
    data: associatedMembers,
    isLoading: isAssociatedMembersLoading,
    isError: isAssociatedMembersError,
  } = useQuery({
    queryKey: ['organization', organizationId, 'associatedMembers'],
    queryFn: () =>
      getOrganizationAssociatedMembers(organizationId, orgRouteToken),
    enabled: !!organizationId && !!orgRouteToken,
  });

  return {
    data: associatedMembers || [],
    isLoading: isAssociatedMembersLoading,
    isError: isAssociatedMembersError,
  };
}

export function useGetOrganizationMemberRoles(
  linkedOrgs,
  organizationId,
  associatedMembers
) {
  const currentOrg = linkedOrgs?.find(
    (org) => org.organizationId === organizationId
  );
  const memberIds = associatedMembers?.map((member) => member.customerId);
  const {
    data: memberRoles = [],
    isLoading: isLoadingRoles,
    isError: isErrorRoles,
  } = useQuery({
    queryKey: ['organization', organizationId, 'roles'],
    queryFn: () => getOrganizationMemberRoles(memberIds),
    enabled: memberIds?.length > 0 && !!linkedOrgs,
  });

  const memberRolesArray = Array.isArray(memberRoles) ? memberRoles : [];

  const orgMembers = memberRolesArray?.map((member) => {
    const matchingMember = associatedMembers?.find(
      (associatedMember) => associatedMember.customerId === member.customerId
    );
    const { firstName, lastName, email } = matchingMember || {};
    return {
      ...member,
      firstName,
      lastName,
      email,
    };
  });

  const formattedMemberRoles = formatMemberRoles(currentOrg, orgMembers);

  const orgRoles = {
    administrator: formattedMemberRoles['Administrator'] || [],
    organizationPurchaser: formattedMemberRoles['Organization Purchaser'] || [],
    organizationBillingAccountPurchaser:
      formattedMemberRoles['Organization Billing Account Purchaser'] || [],
    organizationBillingManager:
      formattedMemberRoles['Organization Billing Manager'] || [],
    organizationMembers: formattedMemberRoles['Organization Members'] || [],
  };

  return {
    data: orgRoles,
    memberIds: memberIds || [],
    organization: currentOrg,
    isLoading: isLoadingRoles,
    isError: isErrorRoles,
  };
}

export function useGetOrganizationAccessRequests(organizationId, routeToken, options = {}) {
  return useQuery({
    queryKey: ['organization', organizationId, 'accessRequests'],
    queryFn: () => getOrganizationAccessRequests(organizationId, routeToken),
    enabled: !!ENABLE_ACCESS_REQUESTS && !!organizationId && !!routeToken,
    ...options,
  });
}

/* Remove role from member */
export function useRemoveRole(memberIds) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const currentUserId = useSelector(getCurrentUserId);

  return useMutation({
    mutationFn: removeRole,
    onSuccess: (response, variables) => {
      const { organizationId } = variables;
      const queryKey = ['organization', organizationId, 'roles'];
      const updatedCustomer = response?.aggregateId;
      const cachedData = queryClient.getQueryData(queryKey);

      if (cachedData) {
        queryClient.setQueryData(queryKey, (previousData) => {
          const matchingCustomer = previousData.find(
            (data) => data.customerId === updatedCustomer
          );

          const updatedRoles = response?.newState?.roles;
          if (matchingCustomer) {
            const updatedCustomerRoles = {
              ...matchingCustomer,
              assignedRoles: [...updatedRoles],
            };

            const updatedState = previousData.map((data) =>
              data.customerId === updatedCustomer ? updatedCustomerRoles : data
            );
            return updatedState;
          }
        });
      }

      if (variables.successCallbackFn) {
        variables.successCallbackFn();
      }
    },
    onError: (error, variables) => {
      const { email, bannerLocation } = variables;
      dispatch(
        setBannerState({
          data: {
            type: 'error',
            message: intl.Errors.Organizations.Remove_Role_From_User_Error.replace(
              ':email',
              email
            ),
            location: bannerLocation || '',
          },
        })
      );
    },
    onSettled: (_, __, variables) => {
      const { organizationId } = variables;
      dispatch(
        getOrganizationMemberRolesPending({
          userIds: memberIds,
          organizationId,
        })
      );
      dispatch(getUserRolesPending({ customerId: currentUserId }));
    },
  });
}

/* Add role to member */
export function useAddRole(memberRoles, memberIds) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const currentUserId = useSelector(getCurrentUserId);

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const {
        email,
        groupName,
        bannerLocation,
        organizationId,
        roleId,
        accountNumber,
        accountNumbers,
        requestingUser,
      } = payload;

      const formattedRole = formatRoleName[groupName];
      const roleMembers = memberRoles[formattedRole];
      const memberHasRole = roleMembers?.some(
        (memberRole) => memberRole.email === email
      );

      if (memberHasRole && !accountNumber) {
        const errorMessage =
          intl.Errors.Organizations.Add_Role_To_User_Duplicate_Role_Error;
        dispatch(
          setBannerState({
            data: {
              type: 'error',
              message: errorMessage.replace(':email', email),
              location: bannerLocation || '',
            },
          })
        );
        throw new Error(errorMessage);
      }

      const organizationMembers = memberRoles['organizationMembers'];
      const subRoleId =
        accountNumber || accountNumbers
          ? ROLES.find((role) => role.name === 'Billing Account Purchaser')
              ?.roleId
          : null;
      const memberInfo = organizationMembers.find(
        (memberRole) => memberRole.email === email
      );
      const commandRoleId = !accountNumber ? roleId : subRoleId;

      const postRole = (roleId, accountNumber) => {
        const roleCommand = buildAddRoleCommand(
          memberInfo.customerId,
          roleId,
          organizationId,
          accountNumber,
          requestingUser
        );
        return addRole(roleCommand);
      };

      const rolePromises =
        Array.isArray(accountNumbers) && accountNumbers.length >= 1
          ? [
              ...accountNumbers.map((accountNumber) =>
                postRole(subRoleId, accountNumber)
              ),
              postRole(roleId, accountNumber),
            ]
          : [postRole(commandRoleId, accountNumber)];

      const responses = await Promise.all(rolePromises);
      return { responses, payload };
    },
    onSuccess: (data, variables) => {
      const { responses } = data || {};
      const { organizationId, successCallbackFn } = variables;
      const queryKey = ['organization', organizationId, 'roles'];
      const updatedCustomer = responses[responses.length - 1]?.aggregateId;
      const cachedData = queryClient.getQueryData(queryKey);

      if (cachedData) {
        queryClient.setQueryData(queryKey, (previousData) => {
          const matchingCustomer = previousData.find(
            (data) => data.customerId === updatedCustomer
          );

          const findLargestResponse = (responses) => {
            return responses.reduce((largest, current) => {
              return current?.newState?.roles?.length >
                largest?.newState?.roles?.length
                ? current
                : largest;
            }, responses[0]);
          };

          const latestResponse = findLargestResponse(responses);
          const updatedRoles = latestResponse?.newState?.roles;
          if (matchingCustomer) {
            const updatedCustomerRoles = {
              ...matchingCustomer,
              assignedRoles: [...updatedRoles],
            };

            const updatedState = previousData.map((data) =>
              data.customerId === updatedCustomer ? updatedCustomerRoles : data
            );
            return updatedState;
          }
        });
      }

      if (successCallbackFn) {
        successCallbackFn();
      }
    },
    onError: (error, variables) => {
      const { email, bannerLocation, errorCallbackFn } = variables;
      dispatch(
        setBannerState({
          data: {
            type: 'error',
            message: intl.Errors.Organizations.Add_Role_To_User_Error.replace(
              ':email',
              email
            ),
            location: bannerLocation || '',
          },
        })
      );
      errorCallbackFn();
    },
    onSettled: (_, __, variables) => {
      const { organizationId } = variables;
      dispatch(
        getOrganizationMemberRolesPending({
          userIds: memberIds,
          organizationId,
        })
      );
      dispatch(getUserRolesPending({ customerId: currentUserId }));
    },
  });

  return mutation;
}

export function useUpdateOrganizationInfo() {
  return useMutation({
    mutationFn: updateOrganizationInfo,
  });
}

export function useUpdateCustomerPhone() {
  return useMutation({
    mutationFn: updateCustomerPhone,
  });
}

export function useUpdateCustomerEmail() {
  return useMutation({
    mutationFn: updateCustomerEmail,
  });
}

export function useCreateCustomerPhone() {
  return useMutation({
    mutationFn: createCustomerPhone,
  });
}

export function useCreateCustomerEmail() {
  return useMutation({
    mutationFn: createCustomerEmail,
  });
}

export function useUpdateOrganizationProfile({ organizationId, accountId }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const updateOrgMutation = useUpdateOrganizationInfo();
  const updatePhoneMutation = useUpdateCustomerPhone();
  const updateEmailMutation = useUpdateCustomerEmail();
  const createPhoneMutation = useCreateCustomerPhone();
  const createEmailMutation = useCreateCustomerEmail();

  const updateCache = (updater) => {
    queryClient.setQueryData(
      ['organizationProfile', organizationId],
      (oldData) => {
        if (!oldData) return oldData;
        return updater({ ...oldData });
      }
    );
  };

  const mutateProfile = async ({
    values,
    organizationProfileData,
    phoneId,
    emailId,
    countryCodeNumber,
  }) => {
    const results = [];
    const orgInfo = organizationProfileData?.organizationInfo ?? {};

    const defaultAccount = organizationProfileData?.accounts.find(
      (acc) => acc.isDefault === true && acc.isActive === true
    );
    const defaultPhoneNumber = defaultAccount?.accountProfile?.phones?.find(
      (num) => num.isDefault === true && num.category === 'OTHER'
    );
    const customerEmail = defaultAccount?.accountProfile?.emails.find(
      (email) => email.category === 'CUSTOMER'
    );

    const orgInfoChanged =
      values.name !== orgInfo.organizationName ||
      values.website !== orgInfo.website ||
      values.weeklyAttendance !== orgInfo.averageWeeklyAttendance;

    const phoneChanged = values.phone !== defaultPhoneNumber?.phoneNumber;
    const emailChanged = values.email !== customerEmail?.emailAddress;

    if (orgInfoChanged) {
      const promise = updateOrgMutation
        .mutateAsync({
          customerId: organizationId,
          name: values.name,
          website: values.website,
          averageWeeklyAttendance: values.weeklyAttendance,
        })
        .then((data) => ({
          type: 'org',
          success: true,
          data,
        }))
        .catch((error) => ({
          type: 'org',
          success: false,
          error,
        }));

      results.push(promise);
    }

    if (phoneChanged && values.phone) {
      const phonePromise = (phoneId
        ? updatePhoneMutation.mutateAsync({
            customerType: 'ORGANIZATION',
            customerId: organizationId,
            accountId,
            phone: {
              phoneId,
              phoneNumber: values.phone,
              countryCode: countryCodeNumber,
            },
          })
        : createPhoneMutation.mutateAsync({
            customerType: 'ORGANIZATION',
            customerId: organizationId,
            accountId,
            phone: {
              phoneNumber: values.phone,
              isDefault: true,
              countryCode: countryCodeNumber,
            },
          })
      )
        .then((data) => ({
          type: 'phone',
          success: true,
          data,
        }))
        .catch((error) => ({
          type: 'phone',
          success: false,
          error,
        }));

      results.push(phonePromise);
    }

    if (emailChanged && values.email) {
      const emailPromise = (emailId
        ? updateEmailMutation.mutateAsync({
            customerType: 'ORGANIZATION',
            customerId: organizationId,
            accountId,
            email: { id: emailId, emailAddress: values.email },
          })
        : createEmailMutation.mutateAsync({
            customerType: 'ORGANIZATION',
            customerId: organizationId,
            accountId,
            emailAddress: values.email,
            category: 'CUSTOMER',
          })
      )
        .then((data) => ({
          type: 'email',
          success: true,
          data,
        }))
        .catch((error) => ({
          type: 'email',
          success: false,
          error,
        }));

      results.push(emailPromise);
    }

    const settledResults = await Promise.allSettled(results);

    const successfulResults = settledResults
      .filter((r) => r.status === 'fulfilled' && r.value.success)
      .map((r) => r.value);

    if (successfulResults.length > 0) {
      dispatch(updateLinkedOrganizationsPending());
      updateCache((previousData) => {
        let updatedData = { ...previousData };

        successfulResults.forEach((result) => {
          if (result.type === 'org') {
            dispatch(
              updateLinkedOrganizationsSuccess({
                organizationId,
                organizationName: values.name,
              })
            );
            updatedData = {
              ...updatedData,
              profile: {
                ...updatedData.profile,
                OrganizationProfile: {
                  ...updatedData.profile.OrganizationProfile,
                  organizationInfo: {
                    ...updatedData.profile.OrganizationProfile.organizationInfo,
                    organizationName: values.name,
                    website: values.website,
                    averageWeeklyAttendance: values.weeklyAttendance,
                  },
                },
              },
            };
          } else if (result.type === 'phone') {
            const phoneResponse = settledResults.find(
              ({ status, value }) =>
                status === 'fulfilled' && value.type === 'phone'
            )?.value?.data?.data;

            const phones = phoneResponse?.newState?.accounts?.find(
              ({ id }) => id === accountId
            )?.accountProfile?.phones;

            updatedData = {
              ...updatedData,
              profile: {
                ...updatedData.profile,
                OrganizationProfile: {
                  ...updatedData.profile.OrganizationProfile,
                  accounts: updatedData.profile.OrganizationProfile.accounts.map(
                    (account) => {
                      if (account.id === accountId) {
                        return {
                          ...account,
                          accountProfile: {
                            ...account.accountProfile,
                            phones: phones,
                          },
                        };
                      }
                      return account;
                    }
                  ),
                },
              },
            };
          } else if (result.type === 'email') {
            const emailResponse = settledResults.find(
              ({ status, value }) =>
                status === 'fulfilled' && value.type === 'email'
            )?.value?.data?.data;

            const emails = emailResponse?.newState?.accounts?.find(
              ({ id }) => id === accountId
            )?.accountProfile?.emails;

            updatedData = {
              ...updatedData,
              profile: {
                ...updatedData.profile,
                OrganizationProfile: {
                  ...updatedData.profile.OrganizationProfile,
                  accounts: updatedData.profile.OrganizationProfile.accounts.map(
                    (account) => {
                      if (account.id === accountId) {
                        return {
                          ...account,
                          accountProfile: {
                            ...account.accountProfile,
                            emails: emails,
                          },
                        };
                      }
                      return account;
                    }
                  ),
                },
              },
            };
          }
        });
        return updatedData;
      });
    }

    const summary = settledResults.map((r) =>
      r.status === 'fulfilled' ? r.value : r.reason
    );

    return summary;
  };

  return {
    mutateProfile,
    isLoading:
      updateOrgMutation.isPending ||
      updatePhoneMutation.isPending ||
      createPhoneMutation.isPending ||
      updateEmailMutation.isPending ||
      createEmailMutation.isPending,
  };
}

/* Update Organization Tax Exemption */
export function useUpdateTaxExemption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, organizationId }) => {
      return addOrgTaxExemption({ file, organizationId });
    },

    onMutate: async ({ organizationId, file }) => {
      await queryClient.cancelQueries(['organizationProfile', organizationId]);
      const previousData = queryClient.getQueryData([
        'organizationProfile',
        organizationId,
      ]);

      queryClient.setQueryData(
        ['organizationProfile', organizationId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            profile: {
              ...oldData.profile,
              OrganizationProfile: {
                ...oldData.profile.OrganizationProfile,
                organizationInfo: {
                  ...oldData.profile.OrganizationProfile.organizationInfo,
                  hasTaxExemption: true,
                },
                uploadedTaxExemptions: [
                  ...oldData.profile.OrganizationProfile.uploadedTaxExemptions,
                  file,
                ],
              },
            },
          };
        }
      );

      return { previousData };
    },

    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ['organizationProfile', variables.organizationId],
          context.previousData
        );
      }
    },
  });
}

/* Update Organization Tax Legal Name */
export function useUpdateLegalName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, legalName }) => {
      return updateTaxLegalName({ organizationId, legalName });
    },

    onMutate: async ({ organizationId, legalName }) => {
      await queryClient.cancelQueries(['organizationProfile', organizationId]);
      const previousData = queryClient.getQueryData([
        'organizationProfile',
        organizationId,
      ]);

      queryClient.setQueryData(
        ['organizationProfile', organizationId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            profile: {
              ...oldData.profile,
              OrganizationProfile: {
                ...oldData.profile.OrganizationProfile,
                organizationInfo: {
                  ...oldData.profile.OrganizationProfile.organizationInfo,
                  legalName,
                },
              },
            },
          };
        }
      );

      return { previousData };
    },

    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ['organizationProfile', variables.organizationId],
          context.previousData
        );
      }
    },
  });
}

/* Request Access */
export function useRequestAccess() {
  return useMutation({
    mutationFn: requestAccess,
  });
}

/* Get PCO Groups */
export function useGetPCOGroups(routeToken, options) {
  return useQuery({
    queryKey: ['pcoGroups'],
    queryFn: () => getPCOGroups(routeToken),
    staleTime: 30,
    retry: false,
    enabled: ENABLE_PCO_INTEGRATION && !!routeToken,
    ...options,
  });
}

/* GET PCO Group Details */
export function useGetPCOGroupDetails(routeToken, groupId) {
  return useQuery({
    queryKey: ['pcoGroupDetails', groupId],
    queryFn: () => getPCOGroupDetails(routeToken, groupId),
    staleTime: 30,
    retry: false,
    enabled: ENABLE_PCO_INTEGRATION && !!routeToken && !!groupId,
  });
}

/* POST Import PCO Group */
export function useImportPCOGroup() {
  return useMutation({
    mutationFn: (routeToken, groupId, accountId) =>
      importPCOGroup(routeToken, groupId, accountId),
  });
}

/**
 * Hook to fetch details for each group in pcoGroups.
 * @param {Array} pcoGroups - Array of groups, each with a unique `id`.
 * @returns {Array} - Array of query results for each group's details.
 */
export const useFetchGroupDetails = (pcoGroups, routeToken) => {
  const groupQueries = useQueries(
    pcoGroups?.map((group) => ({
      queryKey: ['groupDetails', group.id], // Unique query key for each group.
      queryFn: () => getPCOGroupDetails(routeToken, group.id),
      enabled: !!pcoGroups && !!group.id, // Ensure the query runs only if the group has an ID.
    }))
  );

  return groupQueries;
};
